import { Link } from "react-router-dom";
import { Shield, CheckCircle, Users, Award, ArrowRight, Home, Car, Briefcase, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

export const HomePage = () => {
  const features = [
    {
      icon: Shield,
      title: "Independent Agency",
      description: "As an independent agency, we work for you—not insurance companies—to find the best coverage options",
      color: "bg-blue-500",
    },
    {
      icon: Users,
      title: "Wide Range of Products",
      description: "From auto and home to commercial and specialty insurance, we offer comprehensive coverage solutions",
      color: "bg-green-500",
    },
    {
      icon: Award,
      title: "Exceptional Value",
      description: "Access to multiple carriers means competitive rates and coverage tailored to your unique needs",
      color: "bg-purple-500",
    },
    {
      icon: Heart,
      title: "Personal Service",
      description: "Dedicated professionals who take time to understand your situation and deliver personalized solutions",
      color: "bg-orange-500",
    },
  ];

  const insuranceTypes = [
    {
      icon: Home,
      title: "Personal Insurance",
      description: "Protect your home, auto, and family with comprehensive personal coverage",
      link: "/personal-insurance",
      color: "bg-blue-50 text-blue-600",
    },
    {
      icon: Briefcase,
      title: "Commercial Insurance",
      description: "Safeguard your business with tailored commercial insurance solutions",
      link: "/commercial-insurance",
      color: "bg-slate-50 text-slate-600",
    },
  ];

  const stats = [
    { number: "Independent", label: "Agency Advantage" },
    { number: "7 States", label: "We Serve" },
    { number: "Multiple", label: "Carrier Options" },
    { number: "Personal", label: "Service Focus" },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section section-padding relative">
        {/* Family background image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1742522450616-a2cf0cba1274?w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-blue-900/95"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Your Trusted Insurance Partner
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Independent insurance professionals specializing in personal and commercial protection for MN, WI, IA, ND, SD, TN, and AZ
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-6 text-lg">
                  Get Your Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8 py-6 text-lg">
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-gradient-to-r from-blue-50 to-slate-50 border-b border-gray-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-1">{stat.number}</div>
                <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Independent Agency Highlight */}
      <section className="py-12 bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Your Independent Insurance Partner
            </h2>
            <p className="text-xl text-blue-50 mb-6">
              We specialize in a wide range of insurance products designed to bring exceptional value to our customers. 
              As an independent agency, we have the freedom to shop multiple carriers and find the perfect coverage for you.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">Auto Insurance</span>
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">Home Insurance</span>
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">Commercial Insurance</span>
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">Life Insurance</span>
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">Specialty Coverage</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Color */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Choose an Independent Agency?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience the difference of working with professionals who prioritize your needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="card-hover border-2 hover:border-primary relative overflow-hidden">
                <div className={`absolute top-0 left-0 right-0 h-2 ${feature.color}`}></div>
                <CardContent className="pt-8">
                  <div className={`${feature.color} w-14 h-14 rounded-lg flex items-center justify-center mb-4`}>
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Section with Consultation Image */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.pexels.com/photos/8441820/pexels-photo-8441820.jpeg?w=800&q=80"
                alt="Insurance consultation"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Personalized Insurance Solutions
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Our experienced team takes the time to understand your unique needs and find the perfect insurance solutions. 
                We compare options from multiple top-rated carriers to ensure you get the best coverage at competitive rates.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-green-500 p-1 rounded-full mt-1">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">No Obligation Quotes</h3>
                    <p className="text-muted-foreground">Get personalized quotes with no commitment required</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-500 p-1 rounded-full mt-1">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">Expert Guidance</h3>
                    <p className="text-muted-foreground">Professional advice to help you make informed decisions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-purple-500 p-1 rounded-full mt-1">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">Ongoing Support</h3>
                    <p className="text-muted-foreground">We're here for you throughout your insurance journey</p>
                  </div>
                </div>
              </div>
              <Link to="/contact">
                <Button size="lg" className="font-semibold">
                  Get Your Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Types Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Our Insurance Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive protection for individuals and businesses
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {insuranceTypes.map((type, index) => (
              <Card key={index} className="card-hover border-2 hover:border-primary overflow-hidden">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center mb-6 ${type.color}`}>
                    <type.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-foreground">{type.title}</h3>
                  <p className="text-muted-foreground mb-6">{type.description}</p>
                  <Link to={type.link}>
                    <Button variant="outline" className="group">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Family Protection Visual Section */}
      <section className="relative section-padding overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1742659655744-3504f1152644?w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Protecting Families & Businesses Since 1978
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Your peace of mind is our priority. From your home to your vehicles, from your business to your future—we're here to protect what matters most to you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="font-semibold">
                  Get Your Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold">
                  Schedule Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Committed to Your Peace of Mind
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <p className="text-lg text-muted-foreground">
                      Multiple carrier options for the best coverage and pricing
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <p className="text-lg text-muted-foreground">
                      Personalized service from experienced insurance professionals
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <p className="text-lg text-muted-foreground">
                      Serving communities across 7 states with local expertise
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <p className="text-lg text-muted-foreground">
                      Dedicated support when you need us most
                    </p>
                  </div>
                </div>
                <Link to="/contact" className="mt-8">
                  <Button size="lg" className="font-semibold">
                    Request Your Quote Today
                  </Button>
                </Link>
              </div>
              <div 
                className="relative min-h-[400px] md:min-h-full bg-cover bg-center"
                style={{
                  backgroundImage: "url('https://images.unsplash.com/photo-1588979355313-6711a095465f?w=800&q=80')",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-blue-700/60 flex items-center justify-center">
                  <div className="text-center text-white p-8">
                    <Shield className="h-20 w-20 mx-auto mb-4 drop-shadow-lg" />
                    <h3 className="text-3xl font-bold mb-3">Protecting Families</h3>
                    <p className="text-lg text-blue-100">Building lasting relationships through exceptional service</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Review Section */}
      <section className="py-12 bg-gradient-to-r from-blue-500 to-blue-600">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-6">
              <div className="flex justify-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className="w-8 h-8 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Love Our Service? Share Your Experience!
              </h2>
              <p className="text-xl text-blue-100 mb-6">
                Your feedback helps us serve you better and helps others find quality insurance protection
              </p>
            </div>
            <a
              href="https://share.google/s3cGtVTEHM4Fhmzsd"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                variant="secondary"
                className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
              >
                <svg
                  className="w-6 h-6 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Leave Us a Google Review
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
